import { type ReactNode } from "react";
import { motion } from "framer-motion";
import { fadeLeft, fadeRight, viewportOnce } from "@/lib/motion";
import { PlaceholderImage } from "@/components/ui/PlaceholderImage";
import { AccentColor } from "@/lib/colors";

export interface StepByStepItem {
  number: string;
  title: string;
  body: string;
  color: AccentColor;
  video?: string;
  image?: string;
  media?: ReactNode;
}

interface StepByStepSectionProps {
  steps: StepByStepItem[];
}

export function StepByStepSection({ steps }: StepByStepSectionProps) {
  return (
    <section id="how-it-works" className="py-12 lg:py-20 px-6">
      <div className="mx-auto max-w-7xl space-y-10 lg:space-y-16">
        {steps.map((step, i) => (
          <motion.div
            key={step.number}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={i % 2 === 0 ? fadeLeft : fadeRight}
          >
            <div className={i % 2 === 1 ? "lg:[direction:ltr]" : ""}>
              <span
                className={`font-display text-7xl font-bold text-${step.color} `}
              >
                {step.number}
              </span>
              <h3 className="font-display text-2xl font-bold tracking-tight text-text-primary mt-4 uppercase">
                {step.title}
              </h3>
              <p className="mt-4 text-text-body max-w-md">{step.body}</p>
            </div>
            <div className={i % 2 === 1 ? "lg:[direction:ltr]" : ""}>
              {step.media ? (
                step.media
              ) : step.video ? (
                <video
                  className="w-full rounded-2xl"
                  aria-label={`${step.title}: step demo`}
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source src={step.video} type="video/webm" />
                </video>
              ) : step.image ? (
                <div className="flex items-center justify-center h-[320px]">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="h-full w-auto"
                  />
                </div>
              ) : (
                <PlaceholderImage
                  height="h-[320px]"
                  label={`Step ${step.number}`}
                />
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
